/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { Button, useTheme, Typography } from "@mui/material";
import { getStyles } from "./styles";
import { getStyles as getModalStyles } from "~components/atoms/CustomModal/styles";
import { authFetch } from "~helpers/authFetch";
import  useCronJob  from '../../../hooks/useCronJob';
import Loading from "./Loading";
import Completed from "./Completed";
import Default from "./Default";
import { CustomModal } from "~components/atoms";
import { DownloadIcon } from "~components/Icons";
import type { ChangeEvent } from "react";
import { useProductsContext } from "~contexts/Products";
import { useStreamContext } from "~contexts/Stream";

interface InvalidProduct {
  row: number;
  category: string;
  reference: string;
  productId: string;
  reason: string;
  invalidValue?: string;
  invalidField?: string;
}

interface ResultsProps {
  referencesSearched: number;
  unregisteredTotal: number;
  referencesFound: number;
  totalOnAllCategories: number;
  downloadLink: string;
  invalidProducts?: InvalidProduct[];
  duplicateProductsTotal?: number;
}

interface ProgressDetails {
  status: string;
  totalPages: number;
  pagesCompleted: number;
  referencesSearched: number;
  referencesFound: number;
  totalProducts: number;
  productsSaved: number;
  progressPercent: number;
  categoriesCount?: number;
  rowsProcessed?: number;
  productsProcessed?: number;
  processingMessage?: string;
}

const RESULTS_DEFAULT = {
  referencesSearched: 0,
  unregisteredTotal: 0,
  referencesFound: 0,
  totalOnAllCategories: 0,
  downloadLink: "",
};

const PROGRESS_DETAILS_DEFAULT: ProgressDetails = {
  status: "",
  totalPages: 0,
  pagesCompleted: 0,
  referencesSearched: 0,
  referencesFound: 0,
  totalProducts: 0,
  productsSaved: 0,
  progressPercent: 0,
  categoriesCount: 0,
};

const InputFile = () => {
  const theme: any = useTheme();
  const { setDroppedFile, droppedFile } = useProductsContext();
  const { currentStream } = useStreamContext();
  const [message, setMessage] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);
  const [filename, setFilename] = useState<string>("");
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isCompleted, setCompleted] = useState<boolean>(false);
  const [isModalOpened, setModalOpened] = useState<boolean>(false);
  const [isErrorsModalOpened, setErrorsModalOpened] = useState<boolean>(false);
  const [results, setResults] = useState<ResultsProps>(RESULTS_DEFAULT);
  const [progressDetails, setProgressDetails] = useState<ProgressDetails>(PROGRESS_DETAILS_DEFAULT);
  const [invalidProducts, setInvalidProducts] = useState<InvalidProduct[]>([]);
  const { styles } = getStyles(theme, isCompleted);
  const { modal: modalStyles } = getModalStyles(theme);
  
  const { sendExcelForCronJob } = useCronJob();

  const getUploadStatus = async () => {
    if (!currentStream) return null;
    
    const reqOptions = {
      path: `${process.env.REACT_APP_BACKEND_URL}/ecommerce/products/register/${currentStream.brand}/${currentStream.name}/status`,
      method: "GET",
    };

    return authFetch(reqOptions)
      .then((response) => response.json())
      .catch(console.error);
  };

  const checkProgress = async () => {
    // Manter loading ativo durante verificação
    if (!isLoading) {
      setLoading(true);
    }
    
    const statusData = await getUploadStatus();
    
    console.log('=== checkProgress - Status data ===', statusData);
    
    // Se não há status ainda, aguardar um pouco e tentar novamente
    if (!statusData) {
      console.log('=== No status data yet, retrying in 2s ===');
      // Manter mensagem enquanto aguarda status
      if (!message || message === "Fazendo upload da planilha...") {
        setMessage("Aguardando início do processamento...");
        setProgress(2);
      }
      setTimeout(() => checkProgress(), 2000);
      return;
    }
    
    // Verificar se há erro no status
    if (statusData.status === "error" || statusData.error) {
      setProgress(0);
      setLoading(false);
      setCompleted(false);
      setDroppedFile("");
      setModalOpened(false);
      setErrorsModalOpened(true);
      setInvalidProducts([{
        row: 0,
        category: "",
        reference: "",
        productId: "",
        reason: statusData?.error || statusData?.message || "Falha ao carregar planilha",
        invalidValue: "",
        invalidField: "",
      }]);
      return;
    }
    
    const {
      status,
      totalPages,
      downloadLink,
      pagesCompleted,
      referencesFound,
      referencesSearched,
      referencesUniqueProducts,
      totalOnAllCategories,
      productsSaved,
      totalProducts,
      progressPercent,
      referencesTotal,
      invalidProducts: statusInvalidProducts,
      rowsProcessed,
      productsProcessed,
      processingMessage,
      duplicateProductsCount,
    } = statusData;
    
    // Calcular total de produtos: usar totalProducts se disponível, senão referencesUniqueProducts
    // referencesUniqueProducts = produtos válidos (com categoria e ID/referência) que serão salvos
    const calculatedTotalProducts = totalProducts || totalOnAllCategories || referencesUniqueProducts || 0;
    
    // Para mostrar produtos encontrados, usar referencesUniqueProducts (todos os produtos válidos)
    const productsFound = referencesUniqueProducts || referencesFound || 0;
    
    // Atualizar detalhes do progresso
    setProgressDetails({
      status: status || "",
      totalPages: totalPages || 0,
      pagesCompleted: pagesCompleted || 0,
      referencesSearched: referencesSearched || referencesTotal || 0,
      referencesFound: productsFound, // Usar referencesUniqueProducts como referência
      totalProducts: calculatedTotalProducts,
      productsSaved: productsSaved || 0,
      progressPercent: progressPercent || 0,
      rowsProcessed: rowsProcessed || 0,
      productsProcessed: productsProcessed || 0,
      processingMessage: processingMessage || "",
    });
    
    // Calcular progresso e mensagem detalhada
    let progressCalc = 0;
    let detailedMessage = "";
    
    // Priorizar processingMessage quando disponível (vem do backend)
    // Esta é a mensagem mais importante - vem diretamente do backend com o status atual
    if (processingMessage && processingMessage.trim() !== '') {
      detailedMessage = processingMessage;
      
      // Calcular progresso baseado no status
      if (status === "processing") {
        // Durante leitura/validação: estimar progresso baseado em linhas processadas
        if (rowsProcessed !== undefined && referencesTotal && referencesTotal > 0) {
          // 0-30% para leitura/validação
          progressCalc = Math.min(30, Math.round((rowsProcessed / referencesTotal) * 30));
        } else if (rowsProcessed !== undefined && productsProcessed !== undefined && totalProducts && totalProducts > 0) {
          // Se temos totalProducts, usar como referência
          progressCalc = Math.min(30, Math.round((productsProcessed / totalProducts) * 30));
        } else if (rowsProcessed !== undefined && rowsProcessed > 0) {
          // Estimativa conservadora baseada em linhas processadas
          progressCalc = Math.min(30, Math.round((rowsProcessed / Math.max(rowsProcessed, 10000)) * 30));
        } else {
          progressCalc = 5; // Início do processamento
        }
      } else if (status === "syncing") {
        // Durante inserção: usar progressPercent do backend diretamente (30-100%)
        if (progressPercent !== undefined && progressPercent !== null) {
          // progressPercent vem como 0-100, ajustar para 30-100% no total
          progressCalc = Math.min(100, Math.max(30, 30 + Math.round((progressPercent / 100) * 70)));
        } else if (totalProducts && productsSaved !== undefined && totalProducts > 0) {
          // Calcular baseado em productsSaved/totalProducts (30-100%)
          const percent = Math.round((productsSaved / totalProducts) * 100);
          progressCalc = Math.min(100, Math.max(30, 30 + Math.round((percent / 100) * 70)));
        } else {
          progressCalc = 30; // Início da inserção
        }
      } else {
        // Status desconhecido mas temos mensagem
        progressCalc = Math.max(5, progressCalc);
      }
    } else if (status === "processing" && rowsProcessed !== undefined) {
      // Fallback: calcular progresso de processamento
      detailedMessage = `Processando planilha: ${rowsProcessed} linhas processadas, ${productsProcessed || 0} produtos encontrados`;
      progressCalc = Math.min(30, Math.round((rowsProcessed / Math.max(referencesTotal || 10000, rowsProcessed)) * 30));
    } else if (status === "syncing" && totalProducts && productsSaved !== undefined) {
      // Fallback: calcular progresso de inserção (30-100%)
      const percent = Math.round((productsSaved / totalProducts) * 100);
      progressCalc = Math.min(100, Math.max(30, 30 + Math.round((percent / 100) * 70)));
      const invalidCount = statusInvalidProducts?.length || 0;
      const invalidText = invalidCount > 0 ? ` (${invalidCount} produtos inválidos ignorados)` : '';
      detailedMessage = `Salvando produtos no banco de dados: ${productsSaved}/${totalProducts} produtos (${percent}%)${invalidText}`;
    } else if (totalPages > 0) {
      // Lógica antiga para compatibilidade (PubSub)
      progressCalc = Math.round((pagesCompleted / totalPages) * 100);
      
      if (progressCalc === 100 && status === "pending") {
        const productsToSave = referencesUniqueProducts || 0;
        const invalidCount = statusInvalidProducts?.length || 0;
        const invalidText = invalidCount > 0 ? ` (${invalidCount} produtos inválidos serão ignorados)` : '';
        detailedMessage = `Processamento concluído! Preparando para salvar ${productsToSave} produtos no banco de dados${invalidText}...`;
      } else {
        const foundCount = referencesUniqueProducts || 0;
        const searchedCount = referencesSearched || referencesTotal || 0;
        const unregisteredTotal = Math.max(0, searchedCount - foundCount);
        const invalidText = unregisteredTotal > 0 ? ` | ${unregisteredTotal} inválidos` : '';
        detailedMessage = `Processando planilha: Página ${pagesCompleted}/${totalPages} (${progressCalc}%) | ${foundCount} produtos válidos${invalidText}`;
      }
    } else if (status === "processing" || status === "syncing") {
      // Se temos status mas não temos processingMessage, criar mensagem genérica
      if (status === "processing") {
        detailedMessage = "Processando planilha...";
        progressCalc = 10;
      } else if (status === "syncing") {
        detailedMessage = "Salvando produtos no banco de dados...";
        progressCalc = 30;
      } else {
        detailedMessage = "Processando...";
        progressCalc = 5;
      }
    } else {
      detailedMessage = "Aguardando processamento...";
      progressCalc = 2;
    }
    
    // Sempre atualizar a mensagem e progresso
    setMessage(detailedMessage);
    const statusIntValue = Math.min(100, Math.max(0, parseInt(progressCalc.toString(), 10)));
    setProgress(statusIntValue);
    
    console.log('=== Progress updated ===', {
      status,
      progressCalc: statusIntValue,
      detailedMessage,
      productsSaved,
      totalProducts,
      progressPercent,
      processingMessage,
    });

    // Atualizar produtos inválidos se disponíveis (sempre atualizar, mesmo se vazio)
    if (statusInvalidProducts) {
      setInvalidProducts(statusInvalidProducts);
      console.log('=== Invalid products updated ===', {
        count: statusInvalidProducts.length,
        products: statusInvalidProducts.slice(0, 3), // Log apenas primeiros 3
      });
    }

    if (status === "completed") {
      setProgress(100);
      setLoading(false);
      setCompleted(true);
      setDroppedFile("");
      const unregisteredTotal = (referencesSearched || 0) - (referencesFound || 0);
      setResults({
        downloadLink,
        referencesFound,
        unregisteredTotal,
        referencesSearched: referencesSearched || referencesTotal || 0,
        totalOnAllCategories,
        invalidProducts: statusInvalidProducts || [],
        duplicateProductsTotal: duplicateProductsCount || 0,
      });
      
      // Sempre abrir modal de sucesso (não abrir modal de erros automaticamente)
      // Os erros serão mostrados apenas se o usuário clicar no link no modal de sucesso
      setModalOpened(true);
    } else {
      setTimeout(() => checkProgress(), 2000);
    }
  };
 
  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!currentStream) return;
    
    const file = droppedFile ? droppedFile : e.target.files?.[0];
    const sheet = new FormData();
    sheet.append("brand", currentStream.brand);
    sheet.append("streamname", currentStream.name);
    sheet.append("xlsxfile", file!);

    setMessage("Fazendo upload da planilha...");
    setLoading(true);
    setFilename(file?.name || "");
    setProgress(0);

    await authFetch({
      path: `${process.env.REACT_APP_BACKEND_URL}/ecommerce/products/register/`,
      method: "POST",
      data: sheet,
      contentType: "multipart/form-data",
    })
      .then(async (response) => {
        const data = await response.json();
        // Verificar se a resposta contém erro
        const errorMessage = typeof data.message === 'string' ? data.message : '';
        if (!response.ok || data.error || (errorMessage && (errorMessage.includes('erro') || errorMessage.includes('falha')))) {
          throw new Error(data.error || errorMessage || 'Falha ao carregar planilha');
        }
        sendExcelForCronJob(file);
        // Upload concluído, agora vamos monitorar o progresso
        setMessage("Aguardando início do processamento...");
        setProgress(2);
        return data;
      })
      .then(() => {
        // Iniciar verificação de progresso após upload bem-sucedido
        // O checkProgress vai atualizar a mensagem conforme o processamento avança
        checkProgress();
      })
      .catch((error) => {
        console.error('Error uploading file:', error);
        setProgress(0);
        setLoading(false);
        setCompleted(false);
        setDroppedFile("");
        setModalOpened(false);
        setErrorsModalOpened(true);
        setInvalidProducts([{
          row: 0,
          category: "",
          reference: "",
          productId: "",
          reason: error.message || "Falha ao carregar planilha",
          invalidValue: "",
          invalidField: "",
        }]);
      });
  };

  const onDownloadSheet = () => {
    const link = document.createElement("a");
    link.setAttribute("download", "");
    link.href = results.downloadLink;
    link.click();
    setModalOpened(false);
    setTimeout(() => window.location.reload(), 2000);
  };

  const onCloseModal = () => {
    setModalOpened(false);
    setTimeout(() => window.location.reload(), 2000);
  };

  useEffect(() => {
    if (droppedFile) {
      onFileChange(droppedFile);    
    }
  }, [droppedFile]);

  return (
    <>
      {/* Modal de erros de produtos inválidos */}
      <CustomModal isOpened={isErrorsModalOpened} onClose={() => {
        setErrorsModalOpened(false);
        // Não abrir modal de sucesso automaticamente ao fechar erros
        // O modal de sucesso já foi aberto antes
      }}>
        <div style={{ marginBottom: "24px", maxHeight: "60vh", overflowY: "auto" }}>
          <Typography style={modalStyles.titleStyle}>
            {invalidProducts.length === 1 && invalidProducts[0].row === 0 && !invalidProducts[0].category 
              ? "Falha ao carregar planilha" 
              : "Produtos com erro na planilha"}
          </Typography>
          <Typography
            style={{
              ...modalStyles.textStyle,
              color: theme.palette.grey[400],
              marginBottom: "16px",
            }}
          >
            {invalidProducts.length === 1 && invalidProducts[0].row === 0 && !invalidProducts[0].category
              ? invalidProducts[0].reason
              : `${invalidProducts.length} produto(s) inválido(s) encontrado(s):`}
          </Typography>
          {invalidProducts.length === 1 && invalidProducts[0].row === 0 && !invalidProducts[0].category ? (
            // Erro geral do backend - mostrar apenas a mensagem de erro
            <Typography
              style={{
                fontSize: "14px",
                color: theme.palette.error.main,
                padding: "16px",
                backgroundColor: theme.palette.grey[800],
                borderRadius: "8px",
                border: `1px solid ${theme.palette.error.main}`,
              }}
            >
              {invalidProducts[0].reason}
            </Typography>
          ) : (
            // Erros de produtos específicos - mostrar lista detalhada
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {invalidProducts.map((product, index) => (
                <div
                  key={index}
                  style={{
                    padding: "12px",
                    backgroundColor: theme.palette.grey[800],
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.error.main}`,
                  }}
                >
                  <Typography
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: theme.palette.error.main,
                      marginBottom: "4px",
                    }}
                  >
                    Linha {product.row}: {product.reason}
                  </Typography>
                  {product.invalidField && product.invalidValue && (
                    <Typography 
                      style={{ 
                        fontSize: "11px", 
                        color: theme.palette.error.light,
                        fontWeight: 500,
                        marginBottom: "4px",
                      }}
                    >
                      Campo com problema: {product.invalidField} = "{product.invalidValue}"
                    </Typography>
                  )}
                  <Typography style={{ fontSize: "11px", color: theme.palette.grey[400] }}>
                    Categoria: {product.category !== "(vazio)" ? product.category : "(não informada)"}
                  </Typography>
                  {product.productId !== "(vazio)" && (
                    <Typography style={{ fontSize: "11px", color: theme.palette.grey[400] }}>
                      ID: {product.productId}
                    </Typography>
                  )}
                  {product.reference !== "(vazio)" && (
                    <Typography style={{ fontSize: "11px", color: theme.palette.grey[400] }}>
                      Referência: {product.reference}
                    </Typography>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={modalStyles.buttonsStyle}>
          <Button
            onClick={() => {
              setErrorsModalOpened(false);
            }}
            style={modalStyles.buttonCancelStyle}
          >
            fechar
          </Button>
        </div>
      </CustomModal>

      {/* Modal de sucesso */}
      <CustomModal isOpened={isModalOpened} onClose={onCloseModal}>
        <div style={{ marginBottom: "24px" }}>
          <Typography style={modalStyles.titleStyle}>
            planilha carregada com sucesso
          </Typography>
          <Typography
            style={{
              ...modalStyles.textStyle,
              color: theme.palette.grey[400],
            }}
          >
            {results.referencesSearched} linhas
          </Typography>
          <Typography
            style={{
              ...modalStyles.textStyle,
              color: theme.palette.error.main,
            }}
          >
            {(results.invalidProducts?.length || 0)} linhas com erro
          </Typography>
          {results.duplicateProductsTotal !== undefined && results.duplicateProductsTotal > 0 && (
            <Typography
              style={{
                ...modalStyles.textStyle,
                color: theme.palette.warning.main || theme.palette.grey[400],
              }}
            >
              {results.duplicateProductsTotal} produto(s) repetido(s) na mesma categoria ignorado(s)
            </Typography>
          )}
          <Typography
            style={{
              ...modalStyles.textStyle,
              color: theme.palette.primary.light,
            }}
          >
            {results.referencesFound} produtos adicionados
          </Typography>
          <Typography style={{ ...modalStyles.textStyle, color: theme.palette.grey[0] }}>
            {results.totalOnAllCategories} produtos em todas as categorias
          </Typography>
          {results.invalidProducts && results.invalidProducts.length > 0 && (
            <Typography
              style={{
                ...modalStyles.textStyle,
                color: theme.palette.error.main,
                marginTop: "8px",
                cursor: "pointer",
                textDecoration: "underline",
              }}
              onClick={() => {
                setModalOpened(false);
                setErrorsModalOpened(true);
              }}
            >
              ⚠ {results.invalidProducts.length} produto(s) com erro - Clique para ver detalhes
            </Typography>
          )}
        </div>
        <Typography style={{ ...modalStyles.textStyle, color: theme.palette.grey[400] }}>
          deseja fazer download da planilha de status das referências?
        </Typography>

        <div style={modalStyles.buttonsStyle}>
          <Button
            onClick={() => onDownloadSheet()}
            endIcon={<DownloadIcon />}
            sx={modalStyles.buttonDownloadStyle}
          >
            download
          </Button>
          <Button
            onClick={() => {
              setModalOpened(false);
              setTimeout(() => window.location.reload(), 2000);
            }}
            style={modalStyles.buttonCancelStyle}
          >
            cancelar
          </Button>
        </div>
      </CustomModal>
      <Button variant="contained" component="label" sx={styles.button}>
        {isLoading ? (
          <Loading
            message={message || "Processando planilha..."}
            progress={progress}
            filename={filename}
            isCompleted={isCompleted}
            progressDetails={progressDetails}
          />
        ) : isCompleted ? (
          <Completed isCompleted={isCompleted} filename={filename} />
        ) : (
          <Default onFileChange={onFileChange}  />
        )}
      </Button>
    </>
  );
};

export default InputFile;
